/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
 document.addEventListener('DOMContentLoaded', function() {
    const continueShopping = document.querySelector('.continue-shopping.mbl-continue');
    if (continueShopping) {
        continueShopping.addEventListener('click', function(event) {
            event.preventDefault();
            window.history.back();
        });
    }

    // Add event listener to each radio button

  // Function to compare variant ID and description variant ID
  function compareAndDisplay() {
    // Get the variant ID from the variant picker
    setTimeout(() => {
    const variantPicker = document.querySelector('variant-picker');
    const variantId = variantPicker.variant ? variantPicker.variant.id : null;
    console.log("Selected Variant ID:", variantId);

    // Get all elements with the attribute 'description-variant-id'
    const otherElements = document.querySelectorAll('[description-variant-id]');

    // Hide all elements with the attribute 'description-variant-id'
    otherElements.forEach(otherElement => {
      otherElement.style.display = 'none';
    });

    // Show elements with matching variant ID
    otherElements.forEach(otherElement => {
      if (otherElement.getAttribute('description-variant-id') == variantId) {
        otherElement.style.display = 'block';
      }
    });
    }, 100);
  }

  // Call the function to initially check and set display property
  compareAndDisplay();




    const radioButtons = document.querySelectorAll('.variant-picker-id');
      radioButtons.forEach(radioButton => {
      radioButton.addEventListener('click', compareAndDisplay);
     });

     function footerAccordionMenu() {
        let accordionToggles = document.querySelectorAll('.accordion-toggle');
        if (accordionToggles) {
            accordionToggles.forEach(function(toggle) {
                toggle.addEventListener('click', function() {
                  let content = this.nextElementSibling;
                  let isActive = this.classList.contains('active');
                  this.classList.toggle('active', !isActive);
                  if (!isActive) {
                    if (content.scrollHeight > 0) {
                        content.style.maxHeight = content.scrollHeight + 'px';
                    }
                    } else {
                        content.style.maxHeight = null;
                    }
                });
              });
        }
      }

      function pageMenu() {
        const dropdownToggles = document.querySelectorAll('.menu-link.has-dropdown > a');

        dropdownToggles.forEach(toggle => {
          toggle.addEventListener('click', function(event) {
            event.preventDefault();

            // Get the parent .menu-link.has-dropdown of the clicked link
            const parentDropdown = this.parentNode;

            // Toggle the target class on the parent .menu-link.has-dropdown
            if (parentDropdown.classList.contains('target')) {
              parentDropdown.classList.remove('target');
            } else {
              // Remove the target class from all .menu-link.has-dropdown elements
              document.querySelectorAll('.menu-link.has-dropdown').forEach(el => {
                el.classList.remove('target');
              });
              // Add the target class to the current .menu-link.has-dropdown
              parentDropdown.classList.add('target');
            }

            let dropdown = this.nextElementSibling;
            if (dropdown.style.display === 'block') {
              dropdown.style.display = 'none'; // Hide the dropdown
            } else {
              document.querySelectorAll('.menu-link.has-dropdown .dropdown-child').forEach(el => {
                el.style.display = 'none';
              });
              dropdown.style.display = 'block'; // Show the current dropdown
            }
          });
        });
      }

      pageMenu();

      function detectUrlForMenu() {
        const path = window.location.pathname;
        // Query all navigation links
        const navLinks = document.querySelectorAll('.menu-link a');
        navLinks.forEach(link => {
          if (link.getAttribute('href') === path) {
            const parentEl = link.parentElement.parentElement;
            const grandParentElement = parentEl.parentElement;
            parentEl.style.display = 'block';
            grandParentElement.classList.add('target');
          }
        });
      }
      function toggleMobileMenu() {
        const toggleBtn = document.querySelector('.toggle-menu');
        const menu = document.querySelector('.menu');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', () => {
                menu.style.display = 'block';
            });
        }
      }
      function dropDownMenu() {
        const el = document.querySelector('.has-dropdown.target');
        if (el) {
            let subEl = el.querySelector('.dropdown-child');
            subEl.style.display = 'block';
        }
      }


      function mobileMenu() {
        if (window.innerWidth <= 640) {
          const hasDropdownLinks = document.querySelectorAll('.menu-link.has-dropdown > a');
          const menu = document.querySelector('.menu');

          hasDropdownLinks.forEach(link => {
            link.addEventListener('click', function(event) {
              event.preventDefault();
              let currentDropdown = event.currentTarget.nextElementSibling;
              let isCurrentlyDisplayed = currentDropdown.style.display === 'block';

              document.querySelectorAll('.dropdown-child').forEach(dropdown => {
                dropdown.style.display = 'none'; // Ensure a clean state before expanding another dropdown
                dropdown.style.maxHeight = null; // Reset maxHeight for a smooth transition
              });
              currentDropdown.style.display = 'block';

              // Only show the clicked dropdown if it was not already open
              if (!isCurrentlyDisplayed) {
                currentDropdown.style.display = 'block';
                currentDropdown.style.maxHeight = currentDropdown.scrollHeight + "px"; // Enable smooth opening
                this.parentElement.classList.add('target'); // Optionally mark the parent as active
              } else {
                // If the dropdown was already open, this click will close it, so remove the 'target' class
                this.parentElement.classList.remove('target');
              }
            });
          });
        }
      }


      detectUrlForMenu();
      dropDownMenu();
      mobileMenu();
      toggleMobileMenu();
      footerAccordionMenu();
      updateShippingMessages();
});


async function updateShippingMessages() {
    let response = await fetch(`/cart`);
    let text = await response.text();

    const parser = new DOMParser();
    const fetchedDoc = parser.parseFromString(text, 'text/html');
    const fetchedShippingMessages = fetchedDoc.querySelectorAll('.free-shipping-bar');
    const currentShippingMessages = document.querySelectorAll('.free-shipping-bar.store-message');
    // Replace each element on the current page with the corresponding one from the fetched page
    currentShippingMessages.forEach((element, index) => {
        if (fetchedShippingMessages[index]) {
            element.replaceWith(fetchedShippingMessages[index].cloneNode(true));
        }
    });
    // Selects the shipping progress bar and initializes DOM elements for tagline updates
const bar = document.querySelector('.free-shipping-bar.store-message');
if (bar) {
    const progress = parseFloat(bar.getAttribute('data-free-shipping-progress'));

    const tagLine = document.querySelector('.tagline');
    const tagLineMobile = document.querySelector('.announcement__text .tagline');

    // Checks if both tagline elements exist before proceeding
    if (tagLine && tagLineMobile) {
        // Function to update the display property of elements
        const updateDisplay = (isVisible) => {
            const displayValue = isVisible ? 'block' : 'none';
            tagLine.style.display = displayValue;
            tagLineMobile.style.display = displayValue;
        };

        // Updates the display based on the shipping progress
        updateDisplay(progress < 1);
    }
}


}


document.addEventListener('on:cart:add', function(event) {
    updateShippingMessages();
});

document.addEventListener('on:cart:after-merge', (event) => {
    updateShippingMessages();
});


/* reduce GroWave review star size*/
if (window.location.pathname.includes("/collections/")) {
    setTimeout(() => {
        console.log("testing working in solomon");
        document.querySelectorAll(".gw-rv-listing-average-placeholder").forEach(el => {
            if (el.shadowRoot) {
                const style = document.createElement("style");
                style.textContent = `
                    .gw-rv-avg-rating__star {
                        width: 15px !important;
                    }
                `;
                el.shadowRoot.appendChild(style);
                // hide empty stars
                el.shadowRoot.querySelectorAll('[aria-label="0 out of 5 ratings"]').forEach(hiddenEl => {
                        hiddenEl.style.display = "none";

                // hide the "(0)" also
                const sibling = hiddenEl.nextElementSibling;
                if (sibling && sibling.classList.contains("gw-rv-listing-average-reviews__count")) {
                    sibling.style.display = "none";
                }
                     
              });
            }
        });
    }, 1000); // 3-second delay
}
